import moment from 'moment'

/**
 * Defines the minimum required number of members in a family.
 */
const MINIMUM_REQUIRED_MEMBERS = 2

export interface FamilyCounts {
  adult: number
  child: number
}

export interface FamilyMember {
  owner?: boolean
  firstName?: string
  lastName?: string
  dateOfBirth?: string
  email?: string
  address?: string
  postalCode?: string
  postalArea?: string
  useOwnerAddress?: boolean
  extraAbroad?: boolean
  extraAbroadId?: string
  purchaseMethod?: 'new' | 'port'
  phone?: string
  simMethod?: 'auto' | 'pickup' | 'input'
  simNumber?: string
  verified?: boolean
  ssn?: string
}

export interface FamilyOwner extends FamilyMember {
  verified?: boolean
  ssn?: string
  phone?: string
}

export interface Family {
  counts: FamilyCounts
  selectedPackageId?: string
  ownerWantsSubscription?: boolean
  members: FamilyMember[]
  digitalSafety?: boolean | null
}

/**
 * Validate that there is a selected package and
 * that there are at least two members in the family.
 */
export const validateFamilyPackages = (family?: Family): boolean => {
  if (!family) return false

  const totalCount = family.counts.adult + family.counts.child

  return totalCount >= MINIMUM_REQUIRED_MEMBERS && Boolean(family.selectedPackageId)
}

/**
 * Validate that the owner is verified and that all the
 * required fields are present and valid.
 */
export const validateFamilyOwner = (owner?: FamilyOwner, family?: Family): boolean => {
  console.log('VAL owner', owner)
  console.log('fam', family)
  
  if (!owner || !family) return false

  return (
    owner.verified &&
    ((family.ownerWantsSubscription && owner.purchaseMethod && owner.firstName && owner.lastName) ||
      ((!family.ownerWantsSubscription || (owner.ssn && owner.ssn.length === 11)) &&
        (!owner.extraAbroad || owner.extraAbroadId) &&
        (!family.ownerWantsSubscription ||
          owner.purchaseMethod === 'new' ||
          (owner.purchaseMethod === 'port' &&
            owner.phone &&
            (owner.simMethod === 'auto' ||
              ((owner.simMethod === 'pickup' || owner.simMethod === 'input') &&
                owner.simNumber))))))
  )
}

/**
 * Validate that the member is set and that all the
 * required fields are present and valid.
 */
export const validateFamilyMember = (member: FamilyMember, family?: Family): boolean => {
  if (member.owner) {
    return validateFamilyOwner(member as FamilyOwner, family)
  }

  return Boolean(
    member.firstName &&
    member.lastName &&
    validateFamilyMemberDateOfBirth(member) &&
    (member.useOwnerAddress || (member.address && member.postalCode && member.postalArea)) &&
    (!member.extraAbroad || member.extraAbroadId) &&
    (member.purchaseMethod === 'new' ||
      (member.purchaseMethod === 'port' &&
        member.phone &&
        (member.simMethod === 'auto' ||
          ((member.simMethod === 'pickup' || member.simMethod === 'input') && member.simNumber))))
  )
}

/**
 * Validate that family with all its members is valid and
 * that all required fields are present and valid.
 */
export const validateFamily = (family: Family): boolean => {
  const totalCount = family.counts.adult + family.counts.child

  return (
    validateFamilyPackages(family) &&
    family.members.every((m) => validateFamilyMember(m, family)) &&
    family.digitalSafety !== null &&
    totalCount >= MINIMUM_REQUIRED_MEMBERS
  )
}

/**
 * Validate that the provided date of birth for the member
 * is valid based on the member type. Adults must be more
 * than 18 years old, children must be less than 18 years old.
 */
export const validateFamilyMemberDateOfBirth = (member: FamilyMember): boolean => {
  if (!member.dateOfBirth) return false
  
  const dateOfBirth = moment(member.dateOfBirth)
  return dateOfBirth.isValid()
}